import { ampli } from '@/ampli';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChevronRightSmallTriangleIcon } from '@/ui/Icons/ChevronRightSmallTriangleIcon';

import { addMetaClickHandlers } from '@/utils/components';
import type { Asset, DiscoveryTokenResult } from '@/utils/discovery/types';

import { buildTokenPageRoute } from '.';
import { UnifiedChildTokenNameCell } from './Cells/UnifiedChildTokenNameCell';
import { STICKY_FIRST_CELL_BG_CLASSES } from './stickyFirstCell';

interface UnifiedTokenRowProps {
  asset: Asset;
  row: Row<Asset | DiscoveryTokenResult | Record<string, never>>;
  preset?: string;
  isLastRow: boolean;
  showPagination?: boolean;
}

export function UnifiedTokenRow({ asset, row, isLastRow, showPagination }: UnifiedTokenRowProps) {
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasPagination = Boolean(showPagination);
  const shouldShowGroupBottomBorder = !isLastRow || hasPagination;

  // Tokens are already sorted on the server side
  const tokens = asset.tokens || [];

  const toggleExpand = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    ampli.tokenGroupToggled({
      token_symbol: asset.symbol,
      ui_action: newExpanded ? 'expand' : 'collapse',
    });
  }, [asset.symbol, isExpanded]);

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleExpand();
    },
    [toggleExpand],
  );

  const handleParentRowClick = useCallback(() => {
    // Toggle expand/collapse if there are multiple tokens
    if (tokens.length > 1) {
      toggleExpand();
    }
  }, [tokens.length, toggleExpand]);

  const handleChildRowClick = useCallback(
    (item: DiscoveryTokenResult) =>
      ({ target }: React.KeyboardEvent | React.MouseEvent) => {
        const isLink = target instanceof HTMLElement && target.closest('a') != null;
        if (!isLink) {
          router.push(buildTokenPageRoute(item.token));
        }
      },
    [router],
  );

  const handleOpenInNewTab = useCallback(
    (item: DiscoveryTokenResult) => () => {
      window.open(
        `${window.location.origin}/tokens/${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[item.token.chainId]}/${item.token.address}`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [],
  );

  if (tokens.length === 0) {
    return null;
  }

  return (
    <>
      {/* Parent Row - Shows aggregated data */}
      <tr
        className="cursor-pointer relative group hover:bg-token-alphalight-200"
        onClick={handleParentRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleParentRowClick();
          }
        }}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${asset.displayName} tokens`}
        aria-expanded={isExpanded}
      >
        {row.getVisibleCells().map((cell, index) => {
          const isFirstCell = index === 0;

          return (
            <td
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'p-0 border-solid border-token-gray-300 h-16 px-1 tablet:first:pl-1 first:sticky first:z-10',
                  ...STICKY_FIRST_CELL_BG_CLASSES,
                  'group-data-[did-scroll=true]/wrapper:first:after:shadow-10 group-data-[did-scroll=true]/wrapper:first:after:absolute',
                  'group-data-[did-scroll=true]/wrapper:first:after:inset-0',
                  'left-0 tablet:last:pr-11 last:pr-4 whitespace-nowrap align-middle',
                  isExpanded || shouldShowGroupBottomBorder ? 'border-b' : '',
                  isFirstCell ? 'relative' : '',
                ],
              })}
              style={{ width: cell.column.getSize() }}
              key={cell.id}
            >
              {/* Connector line segment in parent row - extends down to child rows */}
              {isFirstCell && isExpanded && (
                <div
                  className="absolute w-px bg-token-gray-500"
                  style={{
                    left: 34,
                    top: '50%',
                    height: '50%',
                  }}
                />
              )}
              {isFirstCell ? (
                <div className="flex items-center gap-0.5 h-full">
                  <button
                    onClick={handleToggleExpand}
                    className="text-token-gray-800 hover:text-token-gray-1000 transition-colors p-1 -ml-1 flex-shrink-0"
                    aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                  >
                    <ChevronRightSmallTriangleIcon
                      width={20}
                      height={20}
                      className={isExpanded ? 'text-token-gray-1600' : 'text-token-gray-600'}
                      style={{
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition:
                          'transform 200ms cubic-bezier(0.22, 1, 0.36, 1), color 200ms cubic-bezier(0.22, 1, 0.36, 1)',
                      }}
                    />
                  </button>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ) : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </td>
          );
        })}
      </tr>

      {/* Child Rows - Shows individual tokens when expanded */}
      {isExpanded &&
        tokens.map((tokenItem, tokenIndex) => {
          const isLastChild = tokenIndex === tokens.length - 1;

          return (
            <tr
              key={`${row.id}-child-${tokenItem.token.chainId}-${tokenItem.token.address}`}
              className="cursor-pointer relative group bg-token-gray-200 hover:bg-token-gray-300"
              {...addMetaClickHandlers({
                onMetaClick: handleOpenInNewTab(tokenItem),
                onClick: handleChildRowClick(tokenItem),
              })}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChildRowClick(tokenItem)(e);
                }
              }}
              aria-label={`View ${tokenItem.token.symbol} on ${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[tokenItem.token.chainId]} details`}
            >
              {row.getVisibleCells().map((cell, cellIndex) => {
                const isFirstCell = cellIndex === 0;
                const childRow = {
                  ...row,
                  original: tokenItem,
                };

                return (
                  <td
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: [
                        'p-0 border-solid border-token-gray-300 first:pl-10 mobile:first:pl-[38px] first:sticky first:z-10',
                        // Child rows already use solid `bg-token-gray-*` on the <tr>, so
                        // the sticky first cell can mirror those directly to stay opaque
                        // when scrolling columns slide underneath it.
                        'first:bg-token-gray-200',
                        'group-hover:first:bg-token-gray-300',
                        'group-data-[did-scroll=true]/wrapper:first:after:shadow-10 group-data-[did-scroll=true]/wrapper:first:after:absolute',
                        'group-data-[did-scroll=true]/wrapper:first:after:inset-0',
                        'left-0 tablet:last:pr-11 last:pr-4 whitespace-nowrap align-middle',
                        !isFirstCell && (!isLastChild || shouldShowGroupBottomBorder)
                          ? 'border-b'
                          : '',
                        isFirstCell
                          ? isLastChild && shouldShowGroupBottomBorder
                            ? 'relative border-b'
                            : 'relative'
                          : '',
                      ],
                    })}
                    style={{ width: cell.column.getSize() }}
                    key={`${cell.id}-child-${tokenIndex}`}
                  >
                    {isFirstCell && (
                      <>
                        {/* Continuous vertical line - for last child, ends at curve top so only curve is visible */}
                        <div
                          className="absolute w-px bg-token-gray-500"
                          style={{
                            left: 34,
                            top: 0,
                            // Last child stops at curve top, others run full height
                            height: isLastChild ? 'calc(50% - 10px)' : '100%',
                          }}
                        />
                        {/* Curved horizontal branch - extends to touch chain icon center */}
                        <div
                          className="absolute w-[12px] h-[10px] border-l border-b border-token-gray-500 rounded-bl-[8px]"
                          style={{
                            left: 34,
                            top: 'calc(50% - 10px)',
                          }}
                        />
                        {/* Bottom border that doesn't cross the vertical line (non-last children only) */}
                        {!isLastChild && (
                          <div
                            className="absolute bottom-0 right-0 h-px bg-token-gray-300"
                            style={{ left: 46 }}
                          />
                        )}
                      </>
                    )}
                    <div className="flex items-center h-[46px] px-2">
                      {isFirstCell ? (
                        <div className="flex items-center -ml-0.5">
                          <UnifiedChildTokenNameCell item={tokenItem} />
                        </div>
                      ) : (
                        <span
                          className={
                            cell.column.id.startsWith('change')
                              ? ''
                              : '[&_*]:text-token-gray-800 text-token-gray-800'
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, {
                            ...cell.getContext(),
                            row: childRow,
                          })}
                        </span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          );
        })}
    </>
  );
}
